import { Button, Icon } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './ServiceRequests.module.css';
import type { ServiceRequestsSearch } from './serviceRequestsModel';

/**
 * "Show archived" / "Hide archived" (HS-1548 R2-D2, R2-A5): one toggle beside the filter funnel.
 * Archived requests are hidden by default; the button shows only the archive, and pressed again
 * returns to the active list. The state is the URL's `?view=archived` (absent = active), so it
 * survives a reload and a shared link. Switching keeps the applied filters and returns to page 1.
 *
 * The label names the action and changes with the state, so the button carries **no**
 * `aria-pressed`: a changing label plus a pressed state would announce "Hide archived, pressed",
 * which contradicts itself (WAI-ARIA APG, Button pattern). The brand tint while the archive is
 * shown is visual only.
 */
export function ServiceRequestsArchiveToggle({ search }: { search: ServiceRequestsSearch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showsArchive = search.view === 'archived';

  return (
    <Button
      hierarchy="secondary"
      size="md"
      iconLeading={<Icon name="archive" size={20} />}
      className={classNames(showsArchive && styles['archive-on'])}
      onClick={() => {
        void navigate({
          to: '/requests',
          search: { ...search, view: showsArchive ? undefined : 'archived', page: 1 },
        });
      }}
    >
      {t(
        showsArchive ? 'serviceRequests.views.hideArchived' : 'serviceRequests.views.showArchived',
      )}
    </Button>
  );
}
